import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal,
  TemplateRef,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { QuantityCounterComponent } from '@libs/form/components';
import { IconComponent } from '@libs/components/icons';
import { QuantityCounterInputModel } from '@libs/form/models';
import { ProductPriceVisibilityDirective } from '@libs/shared/ui';
import { ProductListItemBaseModel, ProductModel, ProductPricesModel } from '@libs/shop/models';
import { startWith, switchMap } from 'rxjs';
import { ProductCardCoreComponent } from '../product-card-core';
import { ProductNotOrderableComponent } from '../product-not-orderable';
import { ProductPricesComponent } from '../product-prices';
import { ProductPriceFormatterComponent } from '../product-prices/product-price-formatter';
import { ProductLineItemPriceOverride } from './product-line-item.model';

@Component({
  selector: 'ui-product-line-item',
  templateUrl: './product-line-item.component.html',
  styleUrl: './product-line-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ProductCardCoreComponent,
    ProductNotOrderableComponent,
    ProductPriceFormatterComponent,
    ProductPriceVisibilityDirective,
    ProductPricesComponent,
    QuantityCounterComponent,
    ReactiveFormsModule,
  ],
})
export class ProductLineItemComponent {
  public productItem: InputSignal<ProductListItemBaseModel> = input.required();
  public quantityCounterControl: InputSignal<FormControl<number>> = input.required();

  public readonly: InputSignal<boolean> = input(false);
  public showCheckbox: InputSignal<boolean> = input(false);
  public selectable: InputSignal<boolean> = input(true);
  public showRemove: InputSignal<boolean> = input(true);
  public unavailable: InputSignal<boolean> = input(false);
  public highlightFreeProduct: InputSignal<boolean> = input(false);
  public priceOverride: InputSignal<ProductLineItemPriceOverride | undefined> = input<ProductLineItemPriceOverride>();
  public selected: ModelSignal<boolean> = model(false);
  public isQuantityChangeLoading: InputSignal<boolean> = input(false);
  public isRemoveLoading: InputSignal<boolean> = input(false);
  public enableProductClickTracking: InputSignal<boolean> = input(true);
  public position: InputSignal<number> = input(0);
  public attributesTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();

  public removed: OutputEmitterRef<void> = output();

  protected product: Signal<ProductModel> = computed(() => this.productItem().productData);

  protected isFreeProduct: Signal<boolean> = computed(() => this.product().isFree);

  protected quantityCounterConfig: Signal<QuantityCounterInputModel> = computed(() => ({
    type: 'quantityCounter',
    label: '',
    name: 'quantity',
    value: this.productItem().quantity,
    attributes: {
      min: '1',
    },
  }));

  protected displayPrices: Signal<ProductPricesModel | undefined> = computed(() => this.priceOverride()?.prices ?? this.product().prices);

  protected totalPrice: Signal<number> = computed(() =>
    this.priceOverride()?.total ?? this.quantity() * (this.displayPrices()?.salesUnit ?? 0));

  protected totalDeposit: Signal<number> = computed(() =>
    this.priceOverride()?.deposit ?? this.quantity() * (this.displayPrices()?.salesUnitDeposit ?? 0));

  private readonly quantity: Signal<number> = toSignal(
    toObservable(this.quantityCounterControl).pipe(
      switchMap((control: FormControl<number>) => control.valueChanges.pipe(startWith(control.value))),
    ),
    { initialValue: 0 },
  );
}
